import React, { useState, useEffect } from 'react';
import { history, useIntl } from 'umi';
import { Icon } from 'tea-component';
import useHeartbeat from './hooks/useHeartbeat';
import {
  FETCH_TASK_ID_STATUS_CODE,
  NORMAL_FETCH_TASK_ID_STATUS_CODES,
  startDownloadTask,
  downloadFileByTaskId,
  downloadFileCOSPathByTaskId,
  notificationDownloadError,
} from '@intelligence/services/download';
import { fetchDBIGZTencentQCouldMassage } from '@intelligence/services/cos';
import useCOS, { getUseAccelerateByENV } from '@gp/hooks/useCos/useCOS';
import { getAuthorization, storage } from '@/utils/utils';
import { getTaskId, getDownloadWaitingMsg, saveFile2Local } from './utils';
import { EXCEL_CSV_MIMETYPE, CALCULATE_DOWNLOAD_MAXIMUM_COUNT } from './constants';
import { downloadMobileRankDemographics, downloadMobileDetailDemographics } from '@intelligence/services/topcharts';

import NoPermission from '@intelligence/assets/imgs/common/no-permission.png';
import XlsxIcon from '@intelligence/assets/svg/tipicon/xlsx.svg';

import './index.scss';

/**
 * 公共下载页面
 */
export default function DownloadPage() {
  const {
    entityType: entityTypeOutter,
    taskId: taskIdStr,
    isShowMaxDownloadRowsTips: isShowMaxDownloadRowsTipsOutter = 'true',
    baseSystematicName: baseSystematicStr,
    token, // 下载页面需要单独鉴权, 而不是获取系统的token
    dashboardId, // 承接页面报表id
  } = history?.location?.query || {};

  const authorization = token ?? getAuthorization(); // token有传参就使用传参token, 没有获取系统token
  const entityType = decodeURIComponent(entityTypeOutter as string);
  const baseSystematicName = decodeURIComponent(baseSystematicStr as string);

  const taskId = getTaskId(taskIdStr as string);

  const { formatMessage } = useIntl();

  // 仅预发布、正式 支持COS全球域名加速请求
  const useAccelerate = getUseAccelerateByENV();

  const { getObject } = useCOS({
    useAccelerate,
    baseCOSConfigFetcher: fetchDBIGZTencentQCouldMassage,
    authorizationFetcher: fetchDBIGZTencentQCouldMassage,
  });

  const [fileName, setFileName] = useState('');

  // 下载状态，下载中、下载成功、下载失败，注意这里的枚举值，尽量跟tea组件Icon组件的type 保持一致，避免多余的字符串转换
  const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading');
  // 下载返回提示文案
  const [feedbackMsg, setFeedbackMsg] = useState('');
  // 是否展示最大下载数据条数提示文案（提示文案内容如下）
  // The current maximum number of rows is 100,000, the remaining data will not be downloaded to excel.
  const [isShowMaxDownloadRowsTips, setIsShowMaxDownloadRowsTips] = useState(false);

  const handleSuccess = () => {
    setStatus('success');
  };

  const handleError = (exception?: unknown) => {
    console.warn(exception);
    setFeedbackMsg(formatMessage({ id: 'dbi_download_page_failly_tips' }));
    setStatus('error');
  };

  const handleHeartbeatWorkerMsg = (event: MessageEvent) => {
    try {
      const res = JSON.parse(event.data);
      if (res?.code !== 0 || !NORMAL_FETCH_TASK_ID_STATUS_CODES.includes(res?.data?.task_status)) throw res;
      // 后端已完成数据读取，准好好下载了，可以调用下载文件流的接口
      if (res?.data?.task_status === FETCH_TASK_ID_STATUS_CODE.DOWNLOAD_STATUS_OK) {
        const baseUrl = history.location.pathname.split('/')?.[1];
        // 情报从COS拉取文件，其他系统原处理方式
        if (baseUrl === 'intelligence') {
          downloadFileStreamFromCOS(taskId as number);
        } else {
          // 下载文件流
          downloadFileStream(taskId as number);
        }
        // 开始下载文件流后，停止发心跳
        terminateWorker();
      }
    } catch (exception: any) {
      // 提示下载错误信息
      notificationDownloadError(exception);
      // 下载错误，停止发心跳
      terminateWorker();
      handleError(exception);
    }
  };

  const handleHeartbeatWorkerError = (exception: any) => {
    notificationDownloadError(exception);
    handleError(exception);
    terminateWorker();
  };

  const { postMessageWorker, terminateWorker } = useHeartbeat({
    handler: handleHeartbeatWorkerMsg,
    errorHandler: handleHeartbeatWorkerError,
  });

  // 开启下载任务，获取下载元数据，包含预估时间，文件名， 队列信息等
  const handleStartDownloadTask = async (taskId: number): Promise<void> => {
    try {
      const res = await startDownloadTask({
        task_id: taskId,
      });
      if (res?.code !== 0) throw res;

      const {
        row_count: rowCount = 0,
        estimated_time: estimatedTime = 0,
        max_estimated_time: maxEstimatedTime = 0,
        file_name: fileName = '',
      } = res?.data || {};

      // 更新为下载中状态
      setStatus('loading');

      // 更新文件名
      const decodedFileName = decodeURIComponent(fileName) || entityType;
      setFileName(decodedFileName);

      // 更新下载反馈提示文案
      const feedbackMsgTemp = getDownloadWaitingMsg({
        minSeconds: estimatedTime,
        maxSeconds: maxEstimatedTime,
        total: rowCount,
      });
      setFeedbackMsg(feedbackMsgTemp);

      // 更新是否显示最大下载数据条数提示文案
      if (isShowMaxDownloadRowsTipsOutter === 'true' && rowCount >= CALCULATE_DOWNLOAD_MAXIMUM_COUNT) {
        setIsShowMaxDownloadRowsTips(true);
      }

      // 开启心跳子线程，持续发送心跳包
      postMessageWorker({
        type: 'start',
        params: {
          origin: window?.location?.origin || '',
          authorization,
          taskId,
          baseSystematicName,
          dashboardId,
        },
      });
    } catch (exception: any) {
      notificationDownloadError(exception);
      handleError(exception);
    }
  };

  // 下载文件流
  const downloadFileStream = async (taskId: number): Promise<void> => {
    try {
      const res = await downloadFileByTaskId({ task_id: taskId }, { headers: { Authorization: authorization } });
      if (res?.status !== 200) {
        handleError(new Error('download file res code is not 200'));
        return;
      }
      const headers = res?.headers;
      const contentDisposition = headers?.['content-disposition'];
      let nameTemp = '';
      if (contentDisposition) {
        const list = contentDisposition.split('filename=');
        nameTemp = list[1]?.replaceAll('"', '');
        nameTemp = decodeURIComponent(nameTemp);
      }
      nameTemp = nameTemp || `${entityType}.xlsx`;
      saveFile2Local(res?.data, EXCEL_CSV_MIMETYPE, nameTemp);
      handleSuccess();
    } catch (exception) {
      handleError(exception);
    }
  };

  // 下载文件流（从COS下载文件）
  const downloadFileStreamFromCOS = async (taskId: number): Promise<void> => {
    try {
      const res = await downloadFileCOSPathByTaskId({ task_id: taskId }, { headers: { Authorization: authorization } });
      const filePath = res?.download_cos_url || '';
      const fileFormat = filePath?.substring(filePath?.lastIndexOf('.') + 1);
      const fileName = `${res?.file_name || entityType}.${fileFormat}`;
      const fileBlob = await getObject({ key: filePath });
      saveFile2Local(fileBlob, fileBlob?.type || EXCEL_CSV_MIMETYPE, fileName);

      handleSuccess();
    } catch (exception) {
      handleError(exception);
    }
  };

  const downloadMobileRankDemographicsInPage = async (params: any) => {
    const isCsv = Number(params?.file_format) === 1;
    const ext = isCsv ? '.csv' : '.xlsx';
    const baseName = params?.new_style_donwload_file_name || `${entityType}`;
    // 如果文件名已有后缀则不重复添加
    const hasExt = /\.(xlsx|csv)$/i.test(baseName);
    const name = hasExt ? baseName : `${baseName}${ext}`;
    const decodedFileName = decodeURIComponent(name);
    setFileName(decodedFileName);
    const res = await downloadMobileRankDemographics(params);
    if (res?.status !== 200) {
      handleError(new Error('download file res code is not 200'));
      return;
    }
    const mimeType = isCsv ? 'text/csv' : EXCEL_CSV_MIMETYPE;
    saveFile2Local(res?.data, mimeType, decodedFileName);
    handleSuccess();
  };

  const downloadMobileDetailDemographicsInPage = async (params: any) => {
    const isCsv = Number(params?.file_format) === 1;
    const ext = isCsv ? '.csv' : '.xlsx';
    const baseName = params?.new_style_donwload_file_name || `${entityType}`;
    const hasExt = /\.(xlsx|csv)$/i.test(baseName);
    const name = hasExt ? baseName : `${baseName}${ext}`;
    const decodedFileName = decodeURIComponent(name);
    setFileName(decodedFileName);
    const res = await downloadMobileDetailDemographics(params);
    if (res?.status !== 200) {
      handleError(new Error('download file res code is not 200'));
      return;
    }
    const mimeType = isCsv ? 'text/csv' : EXCEL_CSV_MIMETYPE;
    saveFile2Local(res?.data, mimeType, decodedFileName);
    handleSuccess();
  };

  useEffect(() => {
    // mobile详情页demographics标签页数据源选择sensortower时的下载
    const storagedDetailParams = storage.getItem('dbi_mobile_details_demographics_download_params');
    const { businessType: detailBusinessType, params: detailParams } = storagedDetailParams || {};
    if (detailBusinessType === 'mobile-detail-demographics' && detailParams?.datasource === 'sensortower') {
      storage.removeItem('dbi_mobile_details_demographics_download_params');
      void downloadMobileDetailDemographicsInPage(detailParams);
      return;
    }

    // mobile榜单页demographics标签页数据源选择sensortower时的下载
    const storagedRankParams = storage.getItem('dbi_mobile_rank_demographics_download_params');
    const { businessType: rankBusinessType, params: rankParams } = storagedRankParams || {};
    if (rankBusinessType === 'mobile-demographics' && rankParams?.source === 'sensortower') {
      storage.removeItem('dbi_mobile_rank_demographics_download_params');
      void downloadMobileRankDemographicsInPage(rankParams);
      return;
    }

    if (!taskId) {
      handleError(new Error('taskId is required.'));
      return;
    }

    // 开启下载任务，开启成功后，开启心跳流程
    handleStartDownloadTask(taskId);

    return () => {
      terminateWorker();
    };
  }, []);

  return (
    <div className="dbi-down-page-wrap">
      <div className="dbi-down-page">
        <div className="dbi-down-box">
          <img src={NoPermission} className="dbi-down-box-left" />
          <div className="dbi-down-box-right">
            <div className="dbi-down-box-title">{formatMessage({ id: 'dbi_download_page_title' })}</div>
            <div className="dbi-down-box-desc">
              <div className="dbi-down-box-describe">
                {feedbackMsg}
                <br />
                {formatMessage({ id: 'dbi_download_page_no_close_tips' })}
              </div>
              {/* 最大下载数据条数提示文案 */}
              {isShowMaxDownloadRowsTips && (
                <div className="dbi-down-box-text">
                  {formatMessage({
                    id: 'dbi_download_page_maximum_number_of_rows_tips',
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
        <div className="dbi-down-tipbox">
          <div className="dbi-down-tipbox-lf">
            <img src={XlsxIcon} className="dbi-down-tipbox-img" />
            {/* 文件名 */}
            {fileName}
          </div>
          {/* 下载状态Icon */}
          <Icon type={status} />
        </div>
      </div>
    </div>
  );
}
